import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, Grid2 as Grid, Stack, Typography } from "@mui/material";
import copy from "fast-copy";
import { useSnackbar } from "notistack";
import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CustomDataGrid, MyPaper, SectionPaper } from "../../../components";
import { useAppSelector } from "../../../redux";
import { useLazyGetAllCostingQuery, useUpdateQuoteMutation } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { QuoteCustomItemDetail, QuoteItemDetail, QuoteSliceInterface, TaxDetail, updateQuoteSlice } from "../../../redux/slices/quote";
import { AppRoutes } from "../../../router/routes";
import { getUserSelectedCurrency, UUIDv4 } from "../../../utils";
import { preparePayload } from "../utils";
import { QuoteCustomDetailColumn, QuoteDetailColumn, TaxDetailColumn } from "./utils";
import { formatCurrency } from "pdf-generator";

export const ItemDetails: React.FC<{ quoteId: string, readOnly?: boolean, setIsSaved: any, refetch: () => void }> = ({ quoteId, readOnly, setIsSaved, refetch }) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();

    const store = useAppSelector(store => store);

    const [updateQuote, updateQuoteData] = useUpdateQuoteMutation();
    const [getCosting, getCostingData] = useLazyGetAllCostingQuery();

    const [calculating, setCalculating] = React.useState('');
    const [quote, setQuote] = React.useState<QuoteSliceInterface>(store.quote);
    const [validMessage, setValidMessage] = React.useState<string>('');

    const DEFAULT_CURRENCY = getUserSelectedCurrency(store.auth.userPreferences).value;

    const giveMeErrorMessage = () => {
        let message = `Oops! Something went wrong, Unable to Update Quote Item Details. Try Again Later!`;
        let errorData: any = updateQuoteData.error;
        message = errorData?.data?.internal_error ?? errorData?.data?.title ?? message;
        if (message.includes("The quote currency (")) {
            const quiteCurrency = quote.currency?.value;
            const pricingItems = quote.quote_item_details?.filter(item => item?.pricing?.exchange_rates?.base_currency && (item?.pricing?.exchange_rates?.base_currency !== quiteCurrency))?.map(item => item?.pricing?.exchange_rates?.base_currency).join(", ");
            message = `The quote currency (${quiteCurrency}) does not match the pricing ${pricingItems.includes(",") ? "currencies" : "currency"}${pricingItems.length > 0 ? ` (${pricingItems})` : ''} .Please ensure both currencies are the same before proceeding.`;
        }
        return message;
    };

    const saveItemDetails = () => {
        setValidMessage('');
        const isValid = quote.quote_item_details.every(_ => _.pricing && _.pricing.isCalculated);

        if (!isValid) {
            let _validMessage = 'There are items where the pricing is not calculated yet, please remove or calculate the cost for those pricing before saving.';
            enqueueSnackbar(_validMessage, { variant: "warning" });
            setValidMessage(_validMessage);
            return;
        }

        let payload: any = { ...preparePayload(quote) };

        dispatch(openBackdrop('Updating....'))
        updateQuote({ id: quoteId, payload }).then((res: any) => {
            if (res.data) {
                setIsSaved(true);
                dispatch(updateQuoteSlice(quote));
                enqueueSnackbar('Quote Item Details Updated Successfully!', { variant: "success" });
                refetch();
            } else {
                console.log('error:', res.error)
                enqueueSnackbar(res.error?.data?.title ?? giveMeErrorMessage(), { variant: "error" })
            }
            dispatch(closeBackdrop())
        });

        setSearchParams({});
    };

    // Quote Item Details (Without Custom Details)
    // -------------------------- ///
    const onAddQuoteItemDetail = () => {
        let newState: QuoteSliceInterface = copy(quote);
        newState.quote_item_details.push({
            id: UUIDv4(),
            entityType: null,
            entity: null,
            pricing: null,
            partNumber: '',
            description: '',
            leadTime: '',
            unitCost: 0,
            quantity: 1,
            discount: 0,
            totalCost: 0,
        })
        setQuote({ ...quote, ...newState });
        setIsSaved(false);
    };

    const updateQuoteItemDetails = (id: string, key: keyof QuoteItemDetail, value: never) => {
        let newState: QuoteSliceInterface = copy(quote);
        let total = 0;

        newState.quote_item_details = newState.quote_item_details.map(_ => {
            let totalCost = 0;
            let _key = _;

            if (_.id === id) {
                _key = { ..._, [key]: value }

                if (key === 'entityType') {
                    _key.entity = null;
                    _key.pricing = null;
                    _key.partNumber = '';
                    _key.description = '';
                    _key.quantity = 1;
                    _key.discount = 0;
                }

                if (key === 'entity') {
                    _key.pricing = null;
                    _key.partNumber = '';
                    _key.description = '';
                    _key.quantity = 1;
                    _key.discount = 0;
                }

                if (key === 'pricing') {
                    _key.partNumber = '';
                    _key.description = '';
                    _key.quantity = 1;
                    _key.discount = 0;
                }

                if (key === 'pricing') {
                    _key.partNumber = `${(value as any)?.bom_cost?.sequence_id ?? ""}-r${(value as any)?.bom_cost?.version ?? ""}`;
                    _key.description = (value as any)?.bom_cost?.short_description ?? "";
                }
            }

            totalCost = (_key.pricing as any)?.total_cost ?? 0;
            _key.unitCost = totalCost;

            totalCost = (_key.quantity ?? 1) * totalCost;
            totalCost = totalCost - (((_key.discount ?? 0) / 100) * totalCost)

            total = total + totalCost;
            return { ..._key, totalCost: parseFloat(totalCost.toFixed(2)) }
        });

        newState.total_quote_item_price = parseFloat(total.toFixed(2));

        updateQuoteTotalWithTax(newState);

        setQuote({ ...quote, ...newState })
        setIsSaved(false);
    };

    const onQuoteItemDelete = (id: string) => {
        let newState: QuoteSliceInterface = copy(quote);
        newState.quote_item_details = [];
        newState.total_quote_item_price = 0;
        newState.total_quote_price = 0;
        quote.quote_item_details.forEach(_ => {
            if (_.id !== id) {
                newState.quote_item_details.push(_);
                newState.total_quote_item_price = newState.total_quote_item_price + _.totalCost;
                newState.total_quote_price = newState.total_quote_tax_price + newState.total_quote_item_price;
            }
        });
        updateQuoteTotalWithTax(newState);
        setQuote({ ...quote, ...newState })
        setIsSaved(false);
    };

    const onUpdateQuoteItemDetail = async (id: string, key: keyof QuoteItemDetail, value: any) => {

        updateQuoteItemDetails(id, key, value as never);

        if (key === 'pricing') {
            try {
                setCalculating(id);
                updateQuoteItemDetails(id, 'pricing', { ...value, calculating: true } as never);
                const costing = await getCosting({
                    page: 1,
                    pageSize: 1,
                    bom_id: value?.bom_cost?._id,
                    pricing_id: value?._id,
                    sort_by: 'created_time',
                    sort_order: 'desc'
                }).unwrap();

                if (costing.length) {
                    const cost = costing[0].final_price;
                    const bom_cost = costing[0]?.calculated_cost;

                    updateQuoteItemDetails(id, 'pricing', { ...value, total_cost: cost, bom_cost: bom_cost, costing_id: costing[0]?._id, calculating: false, isCalculated: true } as never);
                } else {
                    updateQuoteItemDetails(id, 'pricing', { ...value, calculating: false, isCalculated: false } as never);
                }
            } catch (error) {
                console.log('error:', error)
                setCalculating('');
            }
        }
    };
    // -------------------------- ///

    // Quote Item Details (With Custom Details)
    // ------------------------- ////
    const onUpdateQuoteCustomItemDetails = (id: string, key: keyof QuoteCustomItemDetail, value: never) => {
        let newState: QuoteSliceInterface = copy(quote);
        let total = 0;

        newState.quote_custom_item_details = newState.quote_custom_item_details.map(_ => {
            let totalCost = 0;
            let _key = _;

            if (_.id === id) {
                _key = { ..._, [key]: value }
            }

            totalCost = _key?.unitCost ?? 0;
            totalCost = (_key.quantity ?? 1) * totalCost;
            totalCost = totalCost - (((_key.discount ?? 0) / 100) * totalCost)

            total = total + totalCost;
            return { ..._key, totalCost: parseFloat(totalCost.toFixed(2)) }
        });

        newState.total_quote_item_price = parseFloat(total.toFixed(2));

        updateQuoteTotalWithTax(newState);

        setQuote({ ...quote, ...newState })
        setIsSaved(false);
    };

    const onUpdateCustomQuote = async (id: string, key: keyof QuoteCustomItemDetail, value: any) => {
        if (key === 'material_id') {
            try {
                setCalculating(id);
                onUpdateQuoteCustomItemDetails(id, 'material_id', { ...value, calculating: true } as never);

                const materialData = value;

                const cost = materialData.money?.cost;
                if (cost) {

                    let newState: QuoteSliceInterface = copy(quote);
                    let total = 0;

                    newState.quote_custom_item_details = newState.quote_custom_item_details.map(_ => {
                        let totalCost = 0;
                        let _key = _;

                        if (_.id === id) {
                            _key = {
                                ..._,
                                [key]: value,
                                unitCost: cost,
                                part_number: `${value?.sequence_id}-r${value?.version}` || "",
                                leadTime: materialData?.lead_time_days || _key?.leadTime || "",
                                description: materialData?.short_description || _key?.description || ""
                            }
                        }

                        totalCost = _key?.unitCost ?? 0;
                        totalCost = (_key.quantity ?? 1) * totalCost;
                        totalCost = totalCost - (((_key.discount ?? 0) / 100) * totalCost)

                        total = total + totalCost;
                        return { ..._key, totalCost: parseFloat(totalCost.toFixed(2)) }
                    });

                    newState.total_quote_item_price = parseFloat(total.toFixed(2));

                    updateQuoteTotalWithTax(newState);

                    setQuote({ ...quote, ...newState })
                    setIsSaved(false);
                } else {
                    onUpdateQuoteCustomItemDetails(id, 'material_id', { ...value } as never);
                }
            } catch (error) {
                console.log('error:', error)
                setCalculating('');
            }
        } else {
            onUpdateQuoteCustomItemDetails(id, key, value as never);
        }
    };

    const onQuoteCustomItemDelete = (id: string) => {
        let newState: QuoteSliceInterface = copy(quote);
        newState.quote_custom_item_details = [];
        newState.total_quote_item_price = 0;
        newState.total_quote_price = 0;
        quote.quote_custom_item_details.forEach(_ => {
            if (_.id !== id) {
                newState.quote_custom_item_details.push(_);
                newState.total_quote_item_price = newState.total_quote_item_price + _.totalCost;
                newState.total_quote_price = newState.total_quote_tax_price + newState.total_quote_item_price;
            }
        });
        updateQuoteTotalWithTax(newState);
        setQuote({ ...quote, ...newState })
        setIsSaved(false);
    };

    const onAddQuoteCustomItemDetail = () => {
        let newState: QuoteSliceInterface = copy(quote);
        newState.quote_custom_item_details.push({
            id: UUIDv4(),
            material_id: null,
            part_number: '',
            description: '',
            quantity: 1,
            unitCost: 0,
            discount: 0,
            totalCost: 0,
            leadTime: '',
        })
        setQuote({ ...quote, ...newState })
        setIsSaved(false);
    };
    // ------------------------- ////

    // Tax Item Details (With Custom Details)
    // ------------------------- ////
    const onUpdateTaxItemDetail = (id: string, key: keyof TaxDetail, value: any) => {
        let newState: QuoteSliceInterface = copy(quote);
        let total = 0;

        newState.tax_details = newState.tax_details.map(_ => {
            let _key = _;
            if (_.id === id) {
                _key = { ..._, [key]: value }
            }

            let totalCost = ((_key.value ?? 0) / 100) * newState.total_quote_item_price

            total = total + totalCost;
            return { ..._key, totalCost: parseFloat(totalCost.toFixed(2)) }
        });

        newState.total_quote_tax_price = parseFloat(total.toFixed(2));
        newState.total_quote_price = newState.total_quote_tax_price + newState.total_quote_item_price;

        setQuote({ ...quote, ...newState })
        setIsSaved(false);
    };

    const onTaxItemDelete = (id: string) => {
        let newState: QuoteSliceInterface = copy(quote);
        newState.tax_details = [];

        quote.tax_details.forEach(_ => {
            if (_.id !== id) {
                newState.tax_details.push(_);
            } else {
                let discount = parseFloat((((_.value ?? 0) / 100) * newState.total_quote_item_price).toFixed(2));
                newState.total_quote_item_price = newState.total_quote_price - discount;
                newState.total_quote_tax_price = newState.total_quote_tax_price - discount;
                newState.total_quote_price = newState.total_quote_tax_price + newState.total_quote_item_price;
            }
        });

        setIsSaved(false);
        setQuote({ ...quote, ...newState })
    };

    const onAddTaxItemDetail = () => {
        let newState: QuoteSliceInterface = copy(quote);
        newState.tax_details.push({
            id: UUIDv4(),
            value: 0,
        })
        setQuote({ ...quote, ...newState })
        setIsSaved(false);
    };
    // ------------------------- ////

    const updateQuoteTotalWithTax = (newState: QuoteSliceInterface) => {
        let total = 0;

        newState.tax_details = newState.tax_details.map((_: any) => {
            let totalCost = ((_.value ?? 0) / 100) * newState.total_quote_item_price
            total = total + totalCost;
            return { ..._, totalCost: parseFloat(totalCost.toFixed(2)) }
        });

        newState.total_quote_tax_price = parseFloat(total.toFixed(2));
        newState.total_quote_price = newState.total_quote_tax_price + newState.total_quote_item_price;

        setQuote({ ...quote, ...newState })
    };

    React.useEffect(() => {
        setQuote(store.quote);
        // eslint-disable-next-line
    }, [store.quote]);

    React.useEffect(() => {
        setIsSaved(true);
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        if (Boolean(searchParams.get("forceSave"))) {
            saveItemDetails();
        };
        // eslint-disable-next-line
    }, [searchParams]);

    return (
        (<Stack
            sx={{
                gap: 1,
                height: "100%",
                pb: 1,
                justifyContent: "space-between"
            }}>
            <Grid container spacing={1}>
                {/* Quote Item Details */}
                <Grid size={12}>
                    <SectionPaper title="Quote Item Details" contentPadding={0} renderButtons={[
                        <Button disabled={readOnly} variant="outlined" size="small" onClick={() => {
                            if (quote.customQuote) {
                                onAddQuoteCustomItemDetail();
                            } else {
                                onAddQuoteItemDetail();
                            }
                        }}>Add Row</Button>
                    ]}>
                        <Box>
                            {quote.customQuote && <CustomDataGrid
                                saveLocal={false}
                                rows={quote.quote_custom_item_details}
                                columns={QuoteCustomDetailColumn(onUpdateCustomQuote, onQuoteCustomItemDelete, quote.currency?.label ?? DEFAULT_CURRENCY, readOnly)}
                                hideFooter={true}
                            />}

                            {!quote.customQuote && <CustomDataGrid
                                saveLocal={false}
                                rows={quote.quote_item_details}
                                columns={QuoteDetailColumn(onUpdateQuoteItemDetail, onQuoteItemDelete, calculating, getCostingData.isLoading, getCostingData.isLoading ? false : (getCostingData.isError || getCostingData?.data?.length === 0), quote.currency?.label ?? DEFAULT_CURRENCY, readOnly)}
                                hideFooter={true}
                            />}
                        </Box>
                    </SectionPaper>
                </Grid>

                {/* Tax Details */}
                <Grid
                    size={{
                        sm: 12,
                        md: 6,
                        lg: 8
                    }}>
                    <SectionPaper title="Tax Details" contentPadding={0} renderButtons={[
                        <Button disabled={readOnly} variant="outlined" size="small" onClick={onAddTaxItemDetail}>Add Row</Button>
                    ]}>
                        <Box sx={{ height: '200px', overflow: "auto" }}>
                            <CustomDataGrid
                                density="compact"
                                saveLocal={false}
                                rows={quote.tax_details}
                                columns={TaxDetailColumn(onUpdateTaxItemDetail, onTaxItemDelete, readOnly)}
                                hideFooter={true}
                            />
                        </Box>
                    </SectionPaper>
                </Grid>

                <Grid
                    size={{
                        sm: 12,
                        md: 6,
                        lg: 4
                    }}>
                    <Stack sx={{
                        gap: 1
                    }}>
                        <SectionPaper title="Summary" hideExpandCollapse={true}>
                            <table style={{ width: "100%" }}>
                                <tr>
                                    <td><Typography variant="body2">Item Cost</Typography></td>
                                    <td> : </td>
                                    <td style={{ textAlign: 'right' }}><Typography variant="body2">{formatCurrency(Number(quote.total_quote_item_price.toFixed(2)), quote.currency?.label ?? DEFAULT_CURRENCY, false)}</Typography></td>
                                </tr>
                                <tr>
                                    <td><Typography variant="body2" >Tax</Typography></td>
                                    <td> : </td>
                                    <td style={{ textAlign: 'right' }}><Typography variant="body2">{formatCurrency(Number(quote.total_quote_tax_price.toFixed(2)), quote.currency?.label ?? DEFAULT_CURRENCY, false)}</Typography></td>
                                </tr>
                                <tr>
                                    <td><hr></hr></td>
                                    <td><hr></hr></td>
                                    <td><hr></hr></td>
                                </tr>
                                <tr>
                                    <td><Typography variant="subtitle1" ><b>Total Cost</b></Typography></td>
                                    <td> : </td>
                                    <td style={{ textAlign: 'right' }}><Typography variant="h6"><b>{formatCurrency(Number(quote.total_quote_price.toFixed(2)), quote.currency?.label ?? DEFAULT_CURRENCY, false)}</b></Typography></td>
                                </tr>
                            </table>
                        </SectionPaper>

                        {/* Alert Box */}
                        {(updateQuoteData.isError) && <Grid size={12}>
                            <Alert severity="error">{giveMeErrorMessage()}</Alert>
                        </Grid>}

                        {/* Validation Error Message */}
                        {validMessage.length > 0 && <Grid size={12}>
                            <Alert severity="warning">{validMessage}</Alert>
                        </Grid>}

                        {
                            !readOnly && <MyPaper padding={0}>
                                <Box sx={{ padding: 2 }}>
                                    <Stack
                                        direction={"row"}
                                        spacing={1}
                                        sx={{
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}>
                                        {/* Cancel Button */}
                                        <Button size="small" disabled={updateQuoteData.isLoading} variant="outlined" sx={{ width: 200 }} color="primary" onClick={() => navigate(AppRoutes.allQuickQuotes)} >
                                            Cancel
                                        </Button>

                                        {/* Create Button */}
                                        <LoadingButton size="small" loading={updateQuoteData.isLoading} variant="contained" sx={{ width: 200 }} color="primary" onClick={saveItemDetails}>
                                            {"Save"}
                                        </LoadingButton>
                                    </Stack>
                                </Box>
                            </MyPaper>
                        }

                    </Stack>
                </Grid>


            </Grid>
        </Stack>)
    );
}